import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGuides } from "@/hooks/use-guides";
import { guidesTestIds as ids } from "@/testids/guidesTestIds";

/**
 * "Getting started" checklist for the home page — a summary of the onboarding
 * guides at /guides, with each row linking into the guide it names.
 *
 * Hidden once every guide is complete, and for roles with no guides available
 * (a viewer cannot build any of them), so a finished account is not nagged.
 */
/**
 * Whether the card will render anything.
 *
 * Exported because Home has to lay itself out around it: its hero is centred
 * with `flex: 1`, which fills the viewport and pushes this card below the fold
 * even when there is room to spare. Home cannot just check "is setup done"
 * itself without restating the rule below, and two copies of it drift.
 */
export function useGettingStartedVisible(): boolean {
  const { guides, total, isLoading, isUnavailable } = useGuides();

  // "Set up" is judged on the auto-detectable guides only. Connecting an agent
  // happens inside that agent and can never be observed, so counting it would
  // pin this card to the top of a finished account's home page forever.
  const setupDone = guides
    .filter((g) => g.guide.autoDetectable)
    .every((g) => g.complete);

  return !(isLoading || isUnavailable || total === 0 || setupDone);
}

export function GettingStartedCard(): React.ReactElement | null {
  const { guides, completedCount, total, isError } = useGuides();
  const visible = useGettingStartedVisible();

  // Nothing to show while the counts that decide completion are still in
  // flight — an all-unchecked card that ticks itself a moment later reads as a
  // bug, and it is the first thing on the page.
  //
  // A failed check does NOT hide it: removing the first-run checklist is worse
  // than showing it with a caveat, and /guides handles the same failure by
  // rendering the list plus a warning — the two surfaces have to agree.
  //
  if (!visible) {
    return null;
  }

  return (
    <div
      className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4"
      data-testid={ids.gettingStartedCard}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-baseline gap-2">
          <h2 className="text-sm font-semibold text-foreground">
            Getting started
          </h2>
          <span
            className="text-xs text-muted-foreground"
            data-testid={ids.gettingStartedProgress}
          >
            {completedCount} of {total}
          </span>
        </div>
        <Link
          to="/guides"
          className="text-xs text-brand underline-offset-4 hover:underline"
          data-testid={ids.gettingStartedViewAllLink}
        >
          All guides
        </Link>
      </div>

      {isError && (
        <p className="text-sm text-warning" data-testid={ids.gettingStartedErrorMessage}>
          We couldn't check part of your setup, so some steps may show as incomplete.
        </p>
      )}

      <ul className="flex flex-col gap-1">
        {guides.map(({ guide, complete }) => (
          <li key={guide.id} data-testid={ids.gettingStartedItem(guide.id)}>
            {/* A real link, so cmd-click and "open in new tab" work — the
                "All guides" link right above behaves that way already. */}
            <Link
              to={`/guides/${guide.id}`}
              data-testid={ids.gettingStartedItemButton(guide.id)}
              className="group flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-muted"
            >
              <span
                aria-hidden
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-full border",
                  complete
                    ? "border-success bg-success/15 text-success"
                    : "border-border",
                )}
              >
                {complete && <Check className="size-3" strokeWidth={2} />}
              </span>
              <span
                className={cn(
                  "min-w-0 flex-1 truncate text-sm",
                  complete
                    ? "text-muted-foreground line-through"
                    : "text-foreground",
                )}
              >
                {guide.title}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {guide.minutes} min
              </span>
              <ArrowRight
                aria-hidden
                className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                strokeWidth={1.5}
              />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
